import { useState, useEffect, useCallback } from 'react';
import { Plus, Users, ChevronRight } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { useRouter } from '@/context/RouterContext';
import { teamsApi } from '@/api/teams';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { Dialog } from '@/components/common/Dialog';
import type { Team } from '@/types/team';

const ROLE_STYLES: Record<string, string> = {
  owner:  'bg-yellow/15 text-yellow border-yellow/30',
  manage: 'bg-purple/15 text-purple border-purple/30',
  write:  'bg-accent/15 text-accent border-accent/30',
  read:   'bg-surface-raised text-text-secondary border-border-default',
};

export function TeamsPage() {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const loadTeams = useCallback(() => {
    setLoading(true);
    teamsApi.listMyTeams()
      .then(res => setTeams(res || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await teamsApi.createTeam(newName.trim());
      setNewName('');
      setShowCreate(false);
      loadTeams();
    } catch (e) {
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      {/* Header — matches TracksOverview / DriversList pattern */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Users size={18} className="text-accent" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-semibold tracking-tight">{t('nav.teams')}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('team.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
        >
          <Plus size={14} />
          {t('team.create')}
        </button>
      </div>

      {/* Create team dialog */}
      <Dialog open={showCreate} onClose={() => { setShowCreate(false); setNewName(''); }} title={t('team.create')}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">{t('team.name')}</label>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              placeholder={t('team.name')}
              className="w-full input-field px-3 py-2.5 text-sm placeholder:text-text-muted focus:border-border-hover"
              autoFocus
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setShowCreate(false); setNewName(''); }}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleCreate}
              disabled={!newName.trim() || creating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
            >
              {creating ? '...' : t('common.create')}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Teams list */}
      {loading ? (
        <LoadingSpinner />
      ) : teams.length === 0 ? (
        <EmptyState
          icon="👥"
          message={t('team.empty')}
        />
      ) : (
        <div className="card overflow-hidden">
          {/* Table Header — desktop */}
          <table className="w-full border-collapse hidden sm:table">
            <thead>
              <tr className="divider">
                <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('team.name')}
                </th>
                <th className="w-[140px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('team.members.field.role')}
                </th>
                <th className="w-[140px] px-4 py-2.5 text-right text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('common.created')}
                </th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team, index) => (
                <tr
                  key={team.id}
                  onClick={() => navigate(`#/teams/${team.id}`)}
                  className={`hover:bg-bg-hover transition-colors cursor-pointer ${index < teams.length - 1 ? 'divider' : ''}`}
                >
                  <td className="px-4 py-3 font-semibold text-text-primary text-sm">
                    {team.name}
                  </td>
                  <td className="px-4 py-3">
                    {team.role && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-medium border ${ROLE_STYLES[team.role] || ROLE_STYLES.read}`}>
                        {t(`team.role.${team.role}` as any)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-xs text-text-muted tabular-nums mono">
                      {new Date(team.createdAt).toLocaleDateString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile View */}
          <div className="sm:hidden">
            {teams.map((team, index) => (
              <div
                key={team.id}
                onClick={() => navigate(`#/teams/${team.id}`)}
                className={`p-4 hover:bg-bg-hover transition-colors cursor-pointer ${index < teams.length - 1 ? 'divider' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-text-primary text-sm truncate">{team.name}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    {team.role && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-medium border ${ROLE_STYLES[team.role] || ROLE_STYLES.read}`}>
                        {t(`team.role.${team.role}` as any)}
                      </span>
                    )}
                    <ChevronRight size={14} className="text-text-muted" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
